import {datadogRum} from '@datadog/browser-rum';

import {assert} from 'Asserts';
import {SliceComponentName} from 'constants/MapsConstants';
import {measureTimingBetweenEvents} from 'performance/BenchlingProfiler';
import {ProfilerEvent, ProfilerEventDomain} from 'performance/ProfilerConstants';

export enum ProfilerMolbioFeatures {
  BULK_ASSEMBLY_FRAGMENTS_TABLE_RENDER = 'bulkAssemblyFragmentsTableRender',
  BULK_ASSEMBLY_CONSTRUCTS_TABLE_RENDER = 'bulkAssemblyConstructsTableRender',
  FORWARD_PAM_SITES_RENDER = 'forwardPamSitesRender',
  CUT_SITES_RENDER = 'cutSitesRender',
  REVERSE_PAM_SITES_RENDER = 'reversePamSitesRender',
  DNA_RENDER = 'dnaRender',
  TITLE_RENDER = 'titleRender',
  EMPTY_PLACEHOLDER_RENDER = 'emptyPlaceholderRender',
  AMINO_ACIDS_RENDER = 'aminoAcidsRender',
  ANTIBODY_CDR_ANNOTATIONS_RENDER = 'antibodyCDRAnnotationsRender',
  ANTIBODY_ALIGNMENT_CDR_ANNOTATIONS_RENDER = 'antibodyAlignmentCDRAnnotationsRender',
  NUCLEOTIDES_SEARCH_RENDER = 'nucleotidesSearchRender',
  TRANSLATIONS_RENDER = 'translationsRender',
  READING_FRAMES_RENDER = 'readingFramesRender',
  ANNOTATIONS_RENDER = 'annotationsRender',
  PRIMERS_RENDER = 'primersRender',
  FORWARD_PRIMERS_RENDER = 'forwardPrimersRender',
  REVERSE_PRIMERS_RENDER = 'reversePrimersRender',
  ORFS_RENDER = 'orfsRender',
  SEQUENCE_AXIS_RENDER = 'sequenceAxisRender',
  HOMOLOGY_REGION_AXIS_RENDER = 'homologyRegionAxisRender',
  TRACE_RENDER = 'traceRender',
  VOTES_RENDER = 'votesRender',
  LINE_RENDER = 'lineRender',
  BIOINTELLIGENT_LINKS_HIGHLIGHT_RENDER = 'biointelligentLinksHighlightRender',
  REVERSE_BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_RENDER = 'reverseBiointelligentLinksHighlightSpaceRender',
  PART_ANNOTATIONS_RENDER = 'partAnnotationsRender',
  PROTEIN_PART_ANNOTATIONS_RENDER = 'proteinPartAnnotationsRender',
  TRANSCRIPTION_ANNOTATIONS_RENDER = 'transcriptionAnnotationsRender',
  GAP_TO_REAL_AXIS_RENDER = 'gapToRealAxisRender',
  ALIGNMENT_COMMENTS_RENDER = 'alignmentCommentsRender',
  PRIMER_ASSEMBLY_MISMATCHES_RENDER = 'primerAssemblyMismatchesRender',
  GOLDEN_GATE_CUT_SITES_RENDER = 'goldenGateCutSitesRender',
  PROTEIN_AXIS_RENDER = 'proteinAxisRender',
  PROTEIN_ALIGNMENT_AXIS_RENDER = 'proteinAlignmentAxisRender',
  AMINO_ACIDS_SEARCH_RENDER = 'aminoAcidsSearchRender',
  CODON_OPTIMIZATION_RENDER = 'codonOptimizationRender',
  PROTEIN_ANNOTATIONS_RENDER = 'proteinAnnotationsRender',
  PROTEIN_ALIGNMENT_ANNOTATIONS_RENDER = 'proteinAlignmentAnnotationsRender',
  PROTEIN_PI_GRAPH_RENDER = 'proteinPIGraphRender',
  PROTEIN_HYDROPHOBICITY_GRAPH_RENDER = 'proteinHydrophobicityGraphRender',
  PROTEIN_ALIGNMENT_IDENTITY_GRAPH_RENDER = 'proteinAlignmentIdentityGraphRender',
  ANTIBODY_NUMBERING_RENDER = 'antibodyNumberingRender',
  STORED_ANTIBODY_NUMBERING_RENDER = 'storedAntibodyNumberingRender',
  PROTEIN_LIABILITY_SITES_RENDER = 'proteinLiabilitySitesRender',
  MODIFIED_NUCLEOTIDES_RENDER = 'modifiedNucleotidesRender',
  MODIFIED_NUCLEOTIDE_SUMMARY_RENDER = 'modifiedNucleotideSummaryRender',
  NUCLEOTIDES_AXIS_OVERLAY_RENDER = 'nucleotidesAxisOverlayRender',
  NUCLEOTIDES_POSITION_HIGHLIGHT_RENDER = 'nucleotidesPositionHighlightRender',
  SELECTION_HIDDEN_WRAPPER_RENDER = 'selectionHiddenWrapperRender',
  FORWARD_TRANSCRIPTION_LINE_RENDER = 'forwardTranscriptionLineRender',
  REVERSE_TRANSCRIPTION_LINE_RENDER = 'reverseTranscriptionLineRender',
  TRANSCRIPTION_ARROW_RENDER = 'transcriptionArrowRender',
  PROTEIN_PARTS_RENDER = 'proteinPartsRender',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerMolbioFeatures]: Function} = {
  [ProfilerMolbioFeatures.BULK_ASSEMBLY_FRAGMENTS_TABLE_RENDER]: processFragmentsTableRender,
  [ProfilerMolbioFeatures.BULK_ASSEMBLY_CONSTRUCTS_TABLE_RENDER]: processConstructsTableRender,
  [ProfilerMolbioFeatures.FORWARD_PAM_SITES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.forwardPamSites
  ),
  [ProfilerMolbioFeatures.CUT_SITES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.cutSites
  ),
  [ProfilerMolbioFeatures.REVERSE_PAM_SITES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.reversePamSites
  ),
  [ProfilerMolbioFeatures.DNA_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.dna),
  [ProfilerMolbioFeatures.TITLE_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.title),
  [ProfilerMolbioFeatures.EMPTY_PLACEHOLDER_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.emptyPlaceholder
  ),
  [ProfilerMolbioFeatures.AMINO_ACIDS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aminoAcids
  ),
  [ProfilerMolbioFeatures.ANTIBODY_CDR_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.antibodyCDRAnnotations
  ),
  [ProfilerMolbioFeatures.ANTIBODY_ALIGNMENT_CDR_ANNOTATIONS_RENDER]:
    makeProcessSequenceMapSliceRenderFunction(SliceComponentName.antibodyAlignmentCDRAnnotations),
  [ProfilerMolbioFeatures.NUCLEOTIDES_SEARCH_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.nucleotidesSearch
  ),
  [ProfilerMolbioFeatures.TRANSLATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.translations
  ),
  [ProfilerMolbioFeatures.READING_FRAMES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.readingFrames
  ),
  [ProfilerMolbioFeatures.ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.annotations
  ),
  [ProfilerMolbioFeatures.PRIMERS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.primers
  ),
  [ProfilerMolbioFeatures.FORWARD_PRIMERS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.forwardPrimers
  ),
  [ProfilerMolbioFeatures.REVERSE_PRIMERS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.reversePrimers
  ),
  [ProfilerMolbioFeatures.ORFS_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.orfs),
  [ProfilerMolbioFeatures.SEQUENCE_AXIS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.sequenceAxis
  ),
  [ProfilerMolbioFeatures.HOMOLOGY_REGION_AXIS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.homologyRegionAxis
  ),
  [ProfilerMolbioFeatures.TRACE_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.trace),
  [ProfilerMolbioFeatures.VOTES_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.votes),
  [ProfilerMolbioFeatures.LINE_RENDER]: makeProcessSequenceMapSliceRenderFunction(SliceComponentName.line),
  [ProfilerMolbioFeatures.BIOINTELLIGENT_LINKS_HIGHLIGHT_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.sequenceBiointelligentLinkHighlights
  ),
  [ProfilerMolbioFeatures.REVERSE_BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_RENDER]:
    makeProcessSequenceMapSliceRenderFunction(
      SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace
    ),
  [ProfilerMolbioFeatures.PART_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.partAnnotations
  ),
  [ProfilerMolbioFeatures.PROTEIN_PART_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequencePartAnnotations
  ),
  [ProfilerMolbioFeatures.GAP_TO_REAL_AXIS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.gapToRealAxis
  ),
  [ProfilerMolbioFeatures.ALIGNMENT_COMMENTS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.alignmentComments
  ),
  [ProfilerMolbioFeatures.PRIMER_ASSEMBLY_MISMATCHES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.primerAssemblyMismatches
  ),
  [ProfilerMolbioFeatures.GOLDEN_GATE_CUT_SITES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.goldenGateCutSites
  ),
  [ProfilerMolbioFeatures.PROTEIN_AXIS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceAxis
  ),
  [ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_AXIS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceAlignmentAxis
  ),
  [ProfilerMolbioFeatures.AMINO_ACIDS_SEARCH_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aminoAcidsSearch
  ),
  [ProfilerMolbioFeatures.CODON_OPTIMIZATION_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.codonOptimization
  ),
  [ProfilerMolbioFeatures.PROTEIN_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceAnnotations
  ),
  [ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceAlignmentAnnotations
  ),
  [ProfilerMolbioFeatures.PROTEIN_PI_GRAPH_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequencePIGraph
  ),
  [ProfilerMolbioFeatures.PROTEIN_HYDROPHOBICITY_GRAPH_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceHydrophobicityGraph
  ),
  [ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_IDENTITY_GRAPH_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceAlignmentIdentityGraph
  ),
  [ProfilerMolbioFeatures.ANTIBODY_NUMBERING_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.antibodyNumbering
  ),
  [ProfilerMolbioFeatures.STORED_ANTIBODY_NUMBERING_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.storedAntibodyNumbering
  ),
  [ProfilerMolbioFeatures.PROTEIN_LIABILITY_SITES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceLiabilitySites
  ),
  [ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDES_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.modifiedNucleotides
  ),
  [ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDE_SUMMARY_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.modifiedNucleotideSummary
  ),
  [ProfilerMolbioFeatures.NUCLEOTIDES_AXIS_OVERLAY_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.nucleotidesAxisOverlay
  ),
  [ProfilerMolbioFeatures.NUCLEOTIDES_POSITION_HIGHLIGHT_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.nucleotidesPositionHighlight
  ),
  [ProfilerMolbioFeatures.SELECTION_HIDDEN_WRAPPER_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.selectionHiddenWrapper
  ),
  [ProfilerMolbioFeatures.FORWARD_TRANSCRIPTION_LINE_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.forwardTranscriptionLine
  ),
  [ProfilerMolbioFeatures.REVERSE_TRANSCRIPTION_LINE_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.reverseTranscriptionLine
  ),
  [ProfilerMolbioFeatures.TRANSCRIPTION_ARROW_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.transcriptionArrow
  ),
  [ProfilerMolbioFeatures.PROTEIN_PARTS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.aaSequenceParts
  ),
  [ProfilerMolbioFeatures.TRANSCRIPTION_ANNOTATIONS_RENDER]: makeProcessSequenceMapSliceRenderFunction(
    SliceComponentName.transcriptionAnnotations
  ),
};

enum ProfilerMolbioActions {
  RENDER_STARTED = 'renderStarted',
  RENDER_FINISHED = 'renderFinished',
}

export const FRAGMENTS_TABLE_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.MOLBIO,
  feature: ProfilerMolbioFeatures.BULK_ASSEMBLY_FRAGMENTS_TABLE_RENDER,
  action: ProfilerMolbioActions.RENDER_STARTED,
};
export const FRAGMENTS_TABLE_RENDER_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.MOLBIO,
  feature: ProfilerMolbioFeatures.BULK_ASSEMBLY_FRAGMENTS_TABLE_RENDER,
  action: ProfilerMolbioActions.RENDER_FINISHED,
};
export const CONSTRUCTS_TABLE_RENDER_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.MOLBIO,
  feature: ProfilerMolbioFeatures.BULK_ASSEMBLY_CONSTRUCTS_TABLE_RENDER,
  action: ProfilerMolbioActions.RENDER_STARTED,
};
export const CONSTRUCTS_TABLE_RENDER_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.MOLBIO,
  feature: ProfilerMolbioFeatures.BULK_ASSEMBLY_CONSTRUCTS_TABLE_RENDER,
  action: ProfilerMolbioActions.RENDER_FINISHED,
};
export const POLYMER_LAYOUT_TO_PROFILER_EVENTS: {
  [sliceName in SliceComponentName]: {startEvent: ProfilerEvent; endEvent: ProfilerEvent};
} = {
  forwardPamSites: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_PAM_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_PAM_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  cutSites: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.CUT_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.CUT_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  reversePamSites: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_PAM_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_PAM_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  dna: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.DNA_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.DNA_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  title: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TITLE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TITLE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  emptyPlaceholder: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.EMPTY_PLACEHOLDER_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.EMPTY_PLACEHOLDER_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  aminoAcids: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.AMINO_ACIDS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.AMINO_ACIDS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  antibodyCDRAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_CDR_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_CDR_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  antibodyAlignmentCDRAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_ALIGNMENT_CDR_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_ALIGNMENT_CDR_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  nucleotidesSearch: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_SEARCH_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_SEARCH_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  translations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSLATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSLATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  readingFrames: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.READING_FRAMES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.READING_FRAMES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  annotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  primers: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  forwardPrimers: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  reversePrimers: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_PRIMERS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  orfs: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ORFS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ORFS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  sequenceAxis: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.SEQUENCE_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.SEQUENCE_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  homologyRegionAxis: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.HOMOLOGY_REGION_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.HOMOLOGY_REGION_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  trace: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRACE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRACE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  votes: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.VOTES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.VOTES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  line: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  sequenceBiointelligentLinkHighlights: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.BIOINTELLIGENT_LINKS_HIGHLIGHT_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.BIOINTELLIGENT_LINKS_HIGHLIGHT_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  reverseSequenceBiointelligentLinkHighlightsSpace: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  partAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PART_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PART_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinPartAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PART_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PART_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  transcriptionAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSCRIPTION_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSCRIPTION_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  gapToRealAxis: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.GAP_TO_REAL_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.GAP_TO_REAL_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  alignmentComments: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ALIGNMENT_COMMENTS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ALIGNMENT_COMMENTS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  primerAssemblyMismatches: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PRIMER_ASSEMBLY_MISMATCHES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PRIMER_ASSEMBLY_MISMATCHES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  goldenGateCutSites: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.GOLDEN_GATE_CUT_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.GOLDEN_GATE_CUT_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinAxis: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinAlignmentAxis: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_AXIS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  aminoAcidsSearch: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.AMINO_ACIDS_SEARCH_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.AMINO_ACIDS_SEARCH_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  codonOptimization: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.CODON_OPTIMIZATION_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.CODON_OPTIMIZATION_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinAlignmentAnnotations: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_ANNOTATIONS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinPIGraph: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PI_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PI_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinHydrophobicityGraph: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_HYDROPHOBICITY_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_HYDROPHOBICITY_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinAlignmentIdentityGraph: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_IDENTITY_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_ALIGNMENT_IDENTITY_GRAPH_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  antibodyNumbering: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_NUMBERING_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.ANTIBODY_NUMBERING_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  storedAntibodyNumbering: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.STORED_ANTIBODY_NUMBERING_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.STORED_ANTIBODY_NUMBERING_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  proteinLiabilitySites: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_LIABILITY_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_LIABILITY_SITES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  modifiedNucleotides: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDES_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDES_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  modifiedNucleotideSummary: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDE_SUMMARY_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.MODIFIED_NUCLEOTIDE_SUMMARY_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  nucleotidesAxisOverlay: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_AXIS_OVERLAY_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_AXIS_OVERLAY_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  nucleotidesPositionHighlight: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_POSITION_HIGHLIGHT_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.NUCLEOTIDES_POSITION_HIGHLIGHT_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  selectionHiddenWrapper: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.SELECTION_HIDDEN_WRAPPER_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.SELECTION_HIDDEN_WRAPPER_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  forwardTranscriptionLine: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_TRANSCRIPTION_LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.FORWARD_TRANSCRIPTION_LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  reverseTranscriptionLine: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_TRANSCRIPTION_LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.REVERSE_TRANSCRIPTION_LINE_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  transcriptionArrow: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSCRIPTION_ARROW_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.TRANSCRIPTION_ARROW_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
  [SliceComponentName.aaSequenceParts]: {
    startEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PARTS_RENDER,
      action: ProfilerMolbioActions.RENDER_STARTED,
    },
    endEvent: {
      domain: ProfilerEventDomain.MOLBIO,
      feature: ProfilerMolbioFeatures.PROTEIN_PARTS_RENDER,
      action: ProfilerMolbioActions.RENDER_FINISHED,
    },
  },
};

export function processMolbioEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerMolbioFeatures;
  assert(
    Object.values(ProfilerMolbioFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

function processFragmentsTableRender(): void {
  const measurement = measureTimingBetweenEvents(
    FRAGMENTS_TABLE_RENDER_STARTED_EVENT,
    FRAGMENTS_TABLE_RENDER_FINISHED_EVENT
  );
  if (measurement == null) {
    return;
  }
  const {time, startEvent: _, endEvent: renderFinishTime} = measurement;

  datadogRum.addAction('Bulk assembly fragments table: Render', {
    time,
    numRows: renderFinishTime.extra?.numFragments,
  });
}

function processConstructsTableRender(): void {
  const measurement = measureTimingBetweenEvents(
    CONSTRUCTS_TABLE_RENDER_STARTED_EVENT,
    CONSTRUCTS_TABLE_RENDER_FINISHED_EVENT
  );
  if (measurement == null) {
    return;
  }

  const {time, startEvent: _, endEvent: renderFinishTime} = measurement;

  datadogRum.addAction('Bulk assembly constructs table: Render', {
    time,
    numRows: renderFinishTime.extra?.numConstructs,
  });
}

function makeProcessSequenceMapSliceRenderFunction(sliceName: SliceComponentName): () => void {
  return function () {
    const measurement = measureTimingBetweenEvents(
      POLYMER_LAYOUT_TO_PROFILER_EVENTS[sliceName].startEvent,
      POLYMER_LAYOUT_TO_PROFILER_EVENTS[sliceName].endEvent
    );
    if (measurement == null) {
      return;
    }

    const {time} = measurement;

    datadogRum.addAction(`${String(sliceName)} slice: Render`, {time});
  };
}
